/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import {
    CashRegisterModelDetailsActionType,
    CashRegisterModelDetailsAction,
    fetchCashRegisterModelDetailsSuccessAction,
    fetchCashRegisterModelCardDataErrorAction,
    fetchVersionCheckSumsSuccessAction,
    startAttestationSuccessAction,
    completeAttestationSuccessAction,
    fetchSoftwareVersionsSuccessAction,
    activateModelSuccessAction,
    eliminateModelSuccessAction,
    activateVersionSuccessAction,
    markVersionOutdatedSuccessAction,
    fetchModelDocumentsSuccessAction,
    submitDocumentsSuccessAction,
    downloadDocumentSuccessAction,
    deleteDocumentsSuccessAction,
    deleteDocumentsErrorAction,
    updateModelDetailsSuccessAction,
    fetchFactoryNumbersListSuccessAction,
} from '../actions/cashRegisterModelsDetailsActions';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { cashRegisterModelsClient } from '../api/cashRegisterModels/cashRegisterModelsClient';
import { AxiosError } from 'axios';
import { NOTIFICATION_TYPES, Notification, downloadFileByUrl, parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';
import { FactoryNumbersListQueryParams } from '../api/cashRegisterModels/cashRegisterModelsClient.typings';
import { CashRegisterModelDetailsState } from '../reducers/cashRegiserModelDetailsReducer';

export const fetchCashRegisterModelDetailsEpic: Epic<CashRegisterModelDetailsAction> = (action$, state) => {
    return action$.pipe(
        ofType(
            CashRegisterModelDetailsActionType.OPEN_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE,
            CashRegisterModelDetailsActionType.START_ATTESTATION_SUCCESS,
            CashRegisterModelDetailsActionType.COMPLETE_ATTESTATION_SUCCESS,
            CashRegisterModelDetailsActionType.ACTIVATE_MODEL_SUCCESS,
            CashRegisterModelDetailsActionType.ELIMINATE_MODEL_SUCCESS,
            CashRegisterModelDetailsActionType.UPDATE_CASH_REGISTER_MODEL_DETAILS_SUCCESS,
        ),
        switchMap(() => {
            const taxNumber = state.value.routerState.route.params.CashRegisterModelTaxNumber;

            return from(cashRegisterModelsClient.fetchCashRegisterModel(taxNumber)).pipe(
                map((resp) => fetchCashRegisterModelDetailsSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterModelCardDataErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.errors.cashRegisterModelNotFound', { searchText: taxNumber }),
                            ),
                        ),
                    ),
                ),
                takeUntil(
                    action$.pipe(
                        ofType(CashRegisterModelDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE),
                    ),
                ),
            );
        }),
    );
};

export const updateModelEpic: Epic<CashRegisterModelDetailsAction> = (action$, state) => {
    return action$.pipe(
        ofType(CashRegisterModelDetailsActionType.UPDATE_CASH_REGISTER_MODEL_DETAILS),
        switchMap((action) => {
            const taxNumber = state.value.routerState.route.params.CashRegisterModelTaxNumber;
            return from(cashRegisterModelsClient.updateCashRegisterModel(taxNumber, action.payload)).pipe(
                map(() => updateModelDetailsSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterModelCardDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.updateModelFailed')),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const startAttestationEpic: Epic<CashRegisterModelDetailsAction> = (action$) => {
    return action$.pipe(
        ofType(CashRegisterModelDetailsActionType.START_ATTESTATION),
        switchMap((action) => {
            return from(cashRegisterModelsClient.startAttestation(action.payload)).pipe(
                map(() => startAttestationSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterModelCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const completeAttestationEpic: Epic<CashRegisterModelDetailsAction> = (action$) => {
    return action$.pipe(
        ofType(CashRegisterModelDetailsActionType.COMPLETE_ATTESTATION),
        switchMap((action) => {
            return from(cashRegisterModelsClient.completeAttestation(action.payload)).pipe(
                map((resp) => {
                    const notification: Notification = {
                        type: 'NOTIFICATION',
                        message: i18n.t('RegistryScreens.attestationCompleted'),
                    };
                    if (!resp.accepted) {
                        notification.type = 'ERROR';
                        const reason = i18n.exists(`cashRegister.attestationErrors.${resp.responseStatus}`)
                            ? `${i18n.t(`cashRegister.attestationErrors.${resp.responseStatus}`)}`
                            : i18n.t('errors.requestFailed');
                        notification.message = i18n.t('RegistryScreens.attestationNotCompleted', { reason });
                    }
                    return completeAttestationSuccessAction(notification);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterModelCardDataErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.attestationNotCompleted', {
                                    reason: i18n.t('errors.requestFailed'),
                                }),
                            ),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const fetchSoftwareVersionsListEpic: Epic<CashRegisterModelDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(
            CashRegisterModelDetailsActionType.FETCH_SOFTWARE_VERSIONS_PAGE_START,
            CashRegisterModelDetailsActionType.FETCH_SOFTWARE_VERSIONS_FULL_LIST_START,
            CashRegisterModelDetailsActionType.START_ATTESTATION_SUCCESS,
            CashRegisterModelDetailsActionType.COMPLETE_ATTESTATION_SUCCESS,
            CashRegisterModelDetailsActionType.ACTIVATE_VERSION_SUCCESS,
            CashRegisterModelDetailsActionType.MARK_VERSION_OUTDATED_SUCCESS,
        ),
        switchMap((action) => {
            const cashRegisterModelTaxNumber = state$.value.routerState.route.params.CashRegisterModelTaxNumber;
            let queryParams = {
                limit: state$.value.cashRegisterModelDetailsReducer.versionsPageParams.limit,
                offset: state$.value.cashRegisterModelDetailsReducer.versionsPageParams.offset,
            };
            if (action.type === CashRegisterModelDetailsActionType.FETCH_SOFTWARE_VERSIONS_FULL_LIST_START)
                queryParams = { limit: null, offset: null };

            return from(
                cashRegisterModelsClient.getModelSoftwareVersions(cashRegisterModelTaxNumber, queryParams),
            ).pipe(
                map((resp) => {
                    return fetchSoftwareVersionsSuccessAction(resp);
                }),
                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterModelCardDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashRegisterModelVersionsNotFound')),
                        ),
                    ),
                ),
                takeUntil(
                    action$.pipe(
                        ofType(CashRegisterModelDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE),
                    ),
                ),
            );
        }),
    );
};

export const fetchVersionCheckSumsEpic: Epic<CashRegisterModelDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegisterModelDetailsActionType.FETCH_VERSION_CHECK_SUMS_START),
        switchMap((action) => {
            const cashRegisterModelTaxNumber = state$.value.routerState.route.params.CashRegisterModelTaxNumber;
            const { versionId, versionName, pageParams } = action.payload;

            return from(
                cashRegisterModelsClient.getVersionCheckSums(cashRegisterModelTaxNumber, versionId, pageParams),
            ).pipe(
                map((resp) => {
                    return fetchVersionCheckSumsSuccessAction({ versionId, checkSumsHistory: resp });
                }),
                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterModelCardDataErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.errors.cashRegisterModelVersionCheckSumsNotFound', {
                                    versionName,
                                }),
                            ),
                        ),
                    ),
                ),
                takeUntil(
                    action$.pipe(
                        ofType(CashRegisterModelDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE),
                    ),
                ),
            );
        }),
    );
};

export const activateModelEpic: Epic<CashRegisterModelDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegisterModelDetailsActionType.ACTIVATE_MODEL),
        switchMap((action) => {
            const cashRegisterModelTaxNumber = state$.value.routerState.route.params.CashRegisterModelTaxNumber;
            return from(cashRegisterModelsClient.activateModel(cashRegisterModelTaxNumber, action.payload)).pipe(
                map(() => activateModelSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterModelCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const eliminateModelEpic: Epic<CashRegisterModelDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegisterModelDetailsActionType.ELIMINATE_MODEL),
        switchMap((action) => {
            const cashRegisterModelTaxNumber = state$.value.routerState.route.params.CashRegisterModelTaxNumber;
            return from(cashRegisterModelsClient.eliminateModel(cashRegisterModelTaxNumber, action.payload)).pipe(
                map(() => eliminateModelSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterModelCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const activateVersionEpic: Epic<CashRegisterModelDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegisterModelDetailsActionType.ACTIVATE_VERSION),
        switchMap((action) => {
            const cashRegisterModelTaxNumber = state$.value.routerState.route.params.CashRegisterModelTaxNumber;
            return from(cashRegisterModelsClient.activateVersion(cashRegisterModelTaxNumber, action.payload)).pipe(
                map(() => activateVersionSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterModelCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const markVersionOutdatedEpic: Epic<CashRegisterModelDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegisterModelDetailsActionType.MARK_VERSION_OUTDATED),
        switchMap((action) => {
            const cashRegisterModelTaxNumber = state$.value.routerState.route.params.CashRegisterModelTaxNumber;
            return from(cashRegisterModelsClient.markVersionOutdated(cashRegisterModelTaxNumber, action.payload)).pipe(
                map(() => markVersionOutdatedSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterModelCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const fetchDocumentsListEpic: Epic<CashRegisterModelDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(
            CashRegisterModelDetailsActionType.FETCH_MODEL_DOCUMENTS_START,
            CashRegisterModelDetailsActionType.APPLY_DOCUMENTS_PAGING,
            CashRegisterModelDetailsActionType.APPLY_DOCUMENTS_SORTING,
            CashRegisterModelDetailsActionType.START_ATTESTATION_SUCCESS,
            CashRegisterModelDetailsActionType.SUBMIT_DOCUMENTS_SUCCESS,
            CashRegisterModelDetailsActionType.DELETE_DOCUMENTS_SUCCESS,
            CashRegisterModelDetailsActionType.DELETE_DOCUMENTS_ERROR, //some files might be deleted
        ),
        switchMap(() => {
            const cashRegisterModelTaxNumber = state$.value.routerState.route.params.CashRegisterModelTaxNumber;
            const queryParams = {
                limit: state$.value.cashRegisterModelDetailsReducer.docsPageParams.limit,
                offset: state$.value.cashRegisterModelDetailsReducer.docsPageParams.offset,
                sortType: state$.value.cashRegisterModelDetailsReducer.docsSortParams.sortType,
                sortOrder: state$.value.cashRegisterModelDetailsReducer.docsSortParams.sortOrder,
            };

            return from(cashRegisterModelsClient.getModelDocuments(cashRegisterModelTaxNumber, queryParams)).pipe(
                map((resp) => {
                    return fetchModelDocumentsSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterModelCardDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashRegisterModelDocumentsNotFound')),
                        ),
                    ),
                ),

                takeUntil(
                    action$.pipe(
                        ofType(CashRegisterModelDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE),
                    ),
                ),
            );
        }),
    );
};

export const submitDocumentsEpic: Epic<CashRegisterModelDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegisterModelDetailsActionType.SUBMIT_DOCUMENTS_START),
        switchMap((action) => {
            const cashRegisterModelTaxNumber = state$.value.routerState.route.params.CashRegisterModelTaxNumber;
            return from(cashRegisterModelsClient.submitDocuments(cashRegisterModelTaxNumber, action.payload)).pipe(
                map(() => submitDocumentsSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterModelCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const downloadDocumentEpic: Epic<CashRegisterModelDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegisterModelDetailsActionType.DOWNLOAD_DOCUMENT_START),
        switchMap((action) => {
            const cashRegisterModelTaxNumber = state$.value.routerState.route.params.CashRegisterModelTaxNumber;
            const fileName = action.payload.name;
            return from(cashRegisterModelsClient.getDocumentUrl(cashRegisterModelTaxNumber, action.payload.id)).pipe(
                switchMap((url) => {
                    return from(downloadFileByUrl(fileName, url)).pipe(
                        map(() => downloadDocumentSuccessAction()),

                        catchError((err: AxiosError) =>
                            of(
                                fetchCashRegisterModelCardDataErrorAction(
                                    parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                                ),
                            ),
                        ),
                    );
                }),
                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterModelCardDataErrorAction(
                            parseError(err, i18n.t('errors.failedToGetUrl', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const deleteDocumentsEpic: Epic<CashRegisterModelDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegisterModelDetailsActionType.DELETE_DOCUMENTS_START),
        switchMap((action) => {
            const cashRegisterModelTaxNumber = state$.value.routerState.route.params.CashRegisterModelTaxNumber;
            return from(cashRegisterModelsClient.deleteDocuments(cashRegisterModelTaxNumber, action.payload)).pipe(
                map(() => {
                    return deleteDocumentsSuccessAction({
                        type: NOTIFICATION_TYPES[1],
                        message: i18n.t('app.filesDeleted'),
                    });
                }),
                catchError((err: AxiosError) =>
                    of(deleteDocumentsErrorAction(parseError(err, i18n.t('errors.failedToDeleteFiles')))),
                ),
            );
        }),
    );
};

export const fetchFactoryNumbersListEpic: Epic<CashRegisterModelDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(
            CashRegisterModelDetailsActionType.FETCH_FACTORY_NUM_START,
            CashRegisterModelDetailsActionType.CHANGE_FACTORY_NUM_FILTER,
            CashRegisterModelDetailsActionType.CHANGE_FACTORY_NUM_PAGING,
        ),
        switchMap(() => {
            const { CashRegisterModelTaxNumber } = state$.value.routerState.route.params;
            const queryParams = makeFactoryNumbersListQueryParamsFromState(
                state$.value.cashRegisterModelDetailsReducer,
            );

            return from(
                cashRegisterModelsClient.listCashRegistersFactoryNumbers(queryParams, CashRegisterModelTaxNumber),
            ).pipe(
                map((resp) => fetchFactoryNumbersListSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterModelCardDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.failedLoadFactoryNumbers')),
                        ),
                    ),
                ),
                takeUntil(
                    action$.pipe(
                        ofType(CashRegisterModelDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE),
                    ),
                ),
            );
        }),
    );
};

function makeFactoryNumbersListQueryParamsFromState(
    state: CashRegisterModelDetailsState,
): FactoryNumbersListQueryParams {
    return {
        offset: state.factoryNumbersListPageParams.offset,
        limit: state.factoryNumbersListPageParams.limit,
        factoryNumber: state.factoryNumbersListFilterParams.factoryNumber,
        used: state.factoryNumbersListFilterParams.used,
    };
}
